import { createContext, createElement, useCallback, useContext, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useLocation, useNavigate } from 'react-router-dom';

export const getMastersBasePath = (pathname) => {
    const parts = pathname.split(/[?#]/)[0].split('/').filter(Boolean);
    return parts[0] === 'masters' && parts[1] ? `/masters/${parts[1]}` : null;
};

export const getModuleBasePath = (pathname) => {
    const parts = pathname.split(/[?#]/)[0].split('/').filter(Boolean);
    if (parts[0] === 'main' && ['upload-document', 'posted-filter'].includes(parts[1])) {
        return `/main/${parts[1]}`;
    }
    if (parts[0]?.toLowerCase() === 'claim' && parts[1]) {
        return `/claim/${parts[1]}`;
    }
    if (parts[0] === 'loyalty' && parts[1]) {
        const depth = parts[1] === 'loyalty-master' && parts[2] ? 3 : 2;
        return `/${parts.slice(0, depth).join('/')}`;
    }
    if (parts[0] === 'reports' && parts[1]) {
        const depth = parts[1] === 'reports-master' && parts[2] ? 3 : 2;
        return `/${parts.slice(0, depth).join('/')}`;
    }
    return ['info', 'approval', 'approvalhierarchy', 'adjustment', 'adjustments', 'claim-submission', 'contract', 'scheme'].includes(parts[0]) && parts[1]
        ? `/${parts[0]}/${parts[1]}`
        : getMastersBasePath(pathname);
};

const ModuleNavigationContext = createContext(null);

export function ModuleNavigationProvider({ active, navigate, children }) {
    const current = useRef({ active, navigate });
    current.current = { active, navigate };
    const pending = useRef(null);
    const requestNavigation = useCallback((to, options, isCurrent) => {
        if (!isCurrent()) return;
        if (current.current.active) {
            current.current.navigate(to, options);
        } else {
            // Finish this module's navigation when its tab is selected again.
            pending.current = { to, options, isCurrent };
        }
    }, []);

    useEffect(() => {
        if (active && pending.current) {
            const request = pending.current;
            pending.current = null;
            if (request.isCurrent()) navigate(request.to, request.options);
        }
    }, [active, navigate]);

    return createElement(ModuleNavigationContext.Provider, { value: requestNavigation }, children);
}

ModuleNavigationProvider.propTypes = {
    active: PropTypes.bool.isRequired,
    navigate: PropTypes.func.isRequired,
    children: PropTypes.node,
};

export const useModulePathname = () => useLocation().pathname;
export const useModuleListPath = () => getModuleBasePath(useLocation().pathname) || -1;

export function useModuleNavigate() {
    const navigate = useNavigate();
    const requestNavigation = useContext(ModuleNavigationContext);
    const { pathname } = useLocation();
    const basePath = getModuleBasePath(pathname);
    const currentPath = useRef(pathname);
    currentPath.current = pathname;
    const mounted = useRef(false);
    useEffect(() => {
        mounted.current = true;
        return () => { mounted.current = false; };
    }, []);

    return useCallback((to, options) => {
        if (!basePath) return navigate(to, options);
        const destination = to === -1 ? basePath : to;
        if (requestNavigation) {
            return requestNavigation(destination, options, () => mounted.current && currentPath.current === pathname);
        }
        if (mounted.current) return navigate(destination, options);
        return undefined;
    }, [basePath, pathname, navigate, requestNavigation]);
}
